import { useState, useEffect, useCallback } from "react";
import FilterComponent from "./FilterComponent";
import DataTable from "./DataTable";
import { PATIENTS_LIST } from "../constants/api";
import {
  PAGINATION,
  MESSAGES,
  FILTER_OPTIONS,
  DEFAULT_PATIENT_SORT,
} from "../constants/app";
import { PageLoading } from "./loading/LoadingIndicators";
import { apiUtils, errorUtils } from "../utils";
import type { Patient, FilterState, SortState } from "../types";

// Session-only persistence key for the patient list sort preference
const SORT_PREFERENCE_KEY = "patientSortPreference";

const readSortPreference = (): SortState => {
  try {
    const stored = sessionStorage.getItem(SORT_PREFERENCE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as SortState;
      if (
        parsed &&
        typeof parsed.field === "string" &&
        (parsed.direction === "asc" || parsed.direction === "desc")
      ) {
        return parsed;
      }
    }
  } catch {
    // Ignore malformed/unavailable session storage and fall back to default
  }
  return {
    field: DEFAULT_PATIENT_SORT.field,
    direction: DEFAULT_PATIENT_SORT.direction,
  };
};

interface HomeProps {
  userEmail: string;
  isAdmin: boolean;
  userId: string;
}

export default function Home({ userEmail, isAdmin, userId }: HomeProps) {
  // Use structured state for filters
  const [filters, setFilters] = useState<FilterState>({
    searchQuery: "",
    riskLevelFilter: FILTER_OPTIONS.ALL,
    ageGroupFilter: FILTER_OPTIONS.ALL,
    genderFilter: FILTER_OPTIONS.ALL,
  });

  const [sortState, setSortState] = useState<SortState>(readSortPreference);

  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Persist sort preference for the duration of the active session
  useEffect(() => {
    try {
      sessionStorage.setItem(SORT_PREFERENCE_KEY, JSON.stringify(sortState));
    } catch {
      // Ignore session storage write failures
    }
  }, [sortState]);

  const handleSortChange = useCallback((next: SortState) => {
    setSortState(next);
  }, []);

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const requestOptions = apiUtils.createRequestOptions("POST", {
        page: PAGINATION.DEFAULT_PAGE,
        limit: PAGINATION.DEFAULT_LIMIT,
      });
      const response = await apiUtils.apiFetch(PATIENTS_LIST, requestOptions);

      const result = await apiUtils.handleResponse<{ data: any[] }>(response);

      // Map patients - backend already filters based on role
      const mappedPatients: Patient[] = (result.data || []).map((p: any) => ({
        userId: p.userId,
        personal_information: p.personal_information,
        physical_details: p.physical_details,
        contact_information: p.contact_information,
        score_summary: p.score_summary,
        vitals: p.vitals,
        health_history: p.healthQuestionnaireResponse,
        hospital_information: p.hospital_information,
        hospital_id: p.hospital_id,
        created_at: p.created_at,
        updated_at: p.updated_at,
      }));

      setPatients(mappedPatients);
      
      // If no patients found, don't show error - just show empty state
      if (mappedPatients.length === 0) {
        setError(null);
      }
    } catch (error: any) {
      const errorMessage = errorUtils.getErrorMessage(error);

      // Expired/invalid sessions are handled globally (session cleared and
      // redirect to Login triggered in apiUtils.handleResponse), so no need to
      // surface an error here.
      setError(
        errorMessage === "Failed to fetch patients"
          ? MESSAGES.ERRORS.FETCH_PATIENTS
          : errorMessage
      );
      errorUtils.logError(error, "Home - fetchPatients");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  // Optimized filter handlers using single state update
  const updateFilter = useCallback((key: keyof FilterState, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSearch = useCallback(
    (query: string) => {
      updateFilter("searchQuery", query);
    },
    [updateFilter]
  );

  const handleRiskLevelChange = useCallback(
    (value: string) => {
      updateFilter("riskLevelFilter", value);
    },
    [updateFilter]
  );

  const handleAgeGroupChange = useCallback(
    (value: string) => {
      updateFilter("ageGroupFilter", value);
    },
    [updateFilter]
  );

  const handleGenderChange = useCallback(
    (value: string) => {
      updateFilter("genderFilter", value);
    },
    [updateFilter]
  );

  // Loading component
  if (loading) {
    return (
      <PageLoading
        message={MESSAGES.LOADING.PATIENTS}
        fullScreen
        className="bg-guardian-10"
      />
    );
  }

  // Error component - only show for actual errors, not empty results
  if (error && patients.length === 0) {
    return (
      <div className="bg-guardian-10 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <svg
              className="w-12 h-12 mx-auto mb-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="font-medium">{error}</p>
          </div>
          <button
            onClick={fetchPatients}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 bg-guardian-10 min-h-full w-full max-w-full">
      <FilterComponent
        onSearch={handleSearch}
        onRiskLevelChange={handleRiskLevelChange}
        onAgeGroupChange={handleAgeGroupChange}
        onGenderChange={handleGenderChange}
      />

      <div className="px-3 md:px-4 pb-4 min-w-0">
        <DataTable
          data={patients}
          searchQuery={filters.searchQuery}
          riskLevelFilter={filters.riskLevelFilter}
          ageGroupFilter={filters.ageGroupFilter}
          genderFilter={filters.genderFilter}
          userEmail={userEmail}
          isAdmin={isAdmin}
          userId={userId}
          onPatientListRefresh={fetchPatients}
          sortState={sortState}
          onSortChange={handleSortChange}
        />
      </div>
    </div>
  );
}
